import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { projectsApi, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Button, Empty, Field, Input, Loader, Modal, Pagination } from "../components/ui";
import { ConfirmDialog, RecordActions } from "../components/actions";
import { ProjectKhataSummary } from "../components/KhataSummary";
import { COPY } from "../utils/copy";
import { emptyProjectKhata } from "../utils/finance";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const emptyForm = { name: "", location: "" };

export default function Projects() {
  const { isBuilder, query, ready } = useBuilderQuery();
  const toast = useToast();
  const { busy, run } = useAsyncAction();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const liveSearch = useDebouncedValue(search);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    if (!ready) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = unwrapList(await projectsApi.list({ ...query, search: liveSearch, page, limit: 12 }));
      setItems(data.items);
      setPagination(data.pagination);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, liveSearch, query.builderId]);

  useEffect(() => {
    setPage(1);
  }, [liveSearch]);

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async (event) => {
    event.preventDefault();
    const result = await run(async () => {
      if (editing) return projectsApi.update(editing.id, form);
      return projectsApi.create(form);
    }, editing ? "Project updated." : "New project added.");

    if (result) {
      closeModal();
      load();
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    const result = await run(() => projectsApi.remove(pendingDelete.id), "Project deleted.");
    if (result) {
      setPendingDelete(null);
      load();
    }
  };

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">Society / plaza / scheme</p>
          <h1>Projects</h1>
        </div>
        {isBuilder ? (
          <Button
            icon="plus"
            onClick={() => {
              setEditing(null);
              setForm(emptyForm);
              setOpen(true);
            }}
          >
            New project
          </Button>
        ) : null}
      </header>

      <form className="filters" onSubmit={(event) => event.preventDefault()}>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Project name" />
      </form>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty title="No projects yet" text="Add a project first, then record sales in it." />
      ) : (
        <div className="sale-grid">
          {items.map((project) => {
            const khata = project.khata || emptyProjectKhata();
            return (
              <article key={project.id} className="sale-card">
                <Link to={`/projects/${project.id}`} className="sale-card-main">
                  <div className="sale-card-top">
                    <h3>{project.name}</h3>
                    {khata.payable > 0 ? <span className="pill pill-credit">Udhar due</span> : null}
                  </div>
                  <p>{project.location}</p>
                  <ProjectKhataSummary khata={khata} variant="card" />
                </Link>
                <div className="row-actions">
                  <Link className="btn btn-ghost btn-sm" to={`/projects/${project.id}/statement`}>
                    Statement
                  </Link>
                <RecordActions
                  onEdit={() => {
                    setEditing(project);
                    setForm({ name: project.name, location: project.location });
                    setOpen(true);
                  }}
                  onDelete={() => setPendingDelete(project)}
                />
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Pagination page={pagination.page} totalPages={pagination.totalPages} onPage={setPage} />

      <Modal
        open={open}
        title={editing ? "Edit project" : "New project"}
        onClose={closeModal}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" form="project-form" disabled={busy}>
              Save
            </Button>
          </>
        }
      >
        <form id="project-form" className="form-grid" onSubmit={save}>
          <Field label="Name">
            <Input
              required
              minLength={2}
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Green Valley"
            />
          </Field>
          <Field label="Location">
            <Input
              required
              minLength={2}
              value={form.location}
              onChange={(event) => setForm((prev) => ({ ...prev, location: event.target.value }))}
              placeholder="Town / city"
            />
          </Field>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete project?"
        text={COPY.deleteProject}
        busy={busy}
        onClose={() => setPendingDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
}
